/**
 * The web renderers. Turns the one token object into the two stylesheets the
 * web apps consume.
 *
 * Nothing here is hand-maintained CSS: `tokens.css` and `tailwind-preset.css`
 * are written by `scripts/buildCss.ts` and re-derived by `generated.test.ts`,
 * which fails if the checked-in files have drifted from this code. That is the
 * mechanism that makes "one source of truth" true rather than aspirational.
 */

import { hexToRgb } from "./contrast";
import type { typeShadow } from "./tokens";
import { palette, tokens } from "./tokens";

/** `surfaceElevated` -> `surface-elevated`. Custom properties are kebab-case. */
export const kebab = (value: string): string =>
  value.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();

const rgba = (hex: string, alpha: number): string => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${Number(alpha.toFixed(3))})`;
};

/** Composes a CSS `box-shadow` from stored shadow geometry. */
export const shadowToCss = (
  shadow: typeShadow,
  color: string,
  boost: number,
): string => {
  if (shadow.opacity === 0) return "none";
  return `0 ${shadow.offsetY}px ${shadow.blur}px ${shadow.spread}px ${rgba(
    color,
    Math.min(1, shadow.opacity * boost),
  )}`;
};

/** A `--name: value;` line, indented for readability in the emitted file. */
const decl = (name: string, value: string | number, indent = "  "): string =>
  `${indent}${name}: ${value};`;

/**
 * The mode-dependent half of the sheet: everything whose value differs between
 * light and dark. Emitted twice in the whole sheet and nowhere else, under
 * `:root` for dark and inside the `prefers-color-scheme: light` block for
 * light.
 */
const renderModeBlock = (mode: "light" | "dark", indent: string): string => {
  const colors = tokens.color[mode];
  const lines: string[] = [];

  lines.push(decl("color-scheme", mode, indent));
  for (const [key, value] of Object.entries(colors)) {
    lines.push(decl(`--sm-color-${kebab(key)}`, value, indent));
  }
  for (const [key, shadow] of Object.entries(tokens.elevation)) {
    lines.push(
      decl(
        `--sm-elevation-${kebab(key)}`,
        shadowToCss(shadow, tokens.shadowColor[mode], tokens.shadowBoost[mode]),
        indent,
      ),
    );
  }
  return lines.join("\n");
};

/** The mode-independent half: palette, space, radius, size, type, motion, z. */
const renderStaticBlock = (indent: string): string => {
  const lines: string[] = [];

  lines.push(
    `${indent}/* Palette — raw scales. Screens use the semantic aliases above. */`,
  );
  for (const [family, scale] of Object.entries(palette)) {
    for (const [step, value] of Object.entries(scale)) {
      lines.push(decl(`--sm-palette-${kebab(family)}-${step}`, value, indent));
    }
  }

  lines.push("");
  lines.push(
    `${indent}/* Marketing surface — scheme-invariant. Emitted HERE, in`,
  );
  lines.push(`${indent}   the mode-independent block, so it can never be`);
  lines.push(`${indent}   overridden in the dark block. See ISSUE-060 D1. */`);
  for (const [key, value] of Object.entries(tokens.marketingColors)) {
    lines.push(decl(`--sm-color-marketing-${kebab(key)}`, value, indent));
  }
  for (const [key, value] of Object.entries(tokens.marketingOverlay)) {
    lines.push(decl(`--sm-marketing-overlay-${kebab(key)}`, value, indent));
  }

  lines.push("");
  lines.push(`${indent}/* Spacing — the 4/8 ladder. Nothing off it. */`);
  for (const [key, value] of Object.entries(tokens.space)) {
    lines.push(decl(`--sm-space-${kebab(key)}`, `${value}px`, indent));
  }

  lines.push("");
  lines.push(`${indent}/* Radius */`);
  for (const [key, value] of Object.entries(tokens.radius)) {
    lines.push(decl(`--sm-radius-${kebab(key)}`, `${value}px`, indent));
  }

  lines.push("");
  lines.push(`${indent}/* Sizes and hard floors */`);
  for (const [key, value] of Object.entries(tokens.size)) {
    lines.push(decl(`--sm-size-${kebab(key)}`, `${value}px`, indent));
  }

  lines.push("");
  lines.push(`${indent}/* Type */`);
  lines.push(decl("--sm-font-sans", tokens.fontFamily.sans, indent));
  for (const [key, style] of Object.entries(tokens.text)) {
    const name = kebab(key);
    lines.push(decl(`--sm-text-${name}-size`, `${style.size}px`, indent));
    lines.push(decl(`--sm-text-${name}-weight`, style.weight, indent));
    lines.push(decl(`--sm-text-${name}-leading`, style.lineHeight, indent));
    lines.push(
      decl(
        `--sm-text-${name}`,
        `${style.weight} ${style.size}px/${style.lineHeight} var(--sm-font-sans)`,
        indent,
      ),
    );
  }

  lines.push("");
  lines.push(`${indent}/* Motion */`);
  for (const [key, value] of Object.entries(tokens.motion.duration)) {
    lines.push(decl(`--sm-motion-${kebab(key)}`, `${value}ms`, indent));
  }
  for (const [key, value] of Object.entries(tokens.motion.easing)) {
    lines.push(decl(`--sm-ease-${kebab(key)}`, value, indent));
  }
  lines.push(
    decl(
      "--sm-spring-smooth-duration",
      `${tokens.motion.spring.smooth.duration}s`,
      indent,
    ),
  );
  lines.push(
    decl(
      "--sm-spring-smooth-bounce",
      tokens.motion.spring.smooth.bounce,
      indent,
    ),
  );

  lines.push("");
  lines.push(
    `${indent}/* Stacking. The error modal outranks every other overlay. */`,
  );
  for (const [key, value] of Object.entries(tokens.zIndex)) {
    lines.push(decl(`--sm-z-${kebab(key)}`, value, indent));
  }

  return lines.join("\n");
};

const BANNER = `/*
 * GENERATED FILE — do not edit.
 *
 * Written by \`bun run build:css\` in packages/theme from src/tokens.ts.
 * \`src/generated.test.ts\` fails if this file and the token object disagree,
 * so editing it by hand breaks the build instead of silently drifting.
 */`;

/**
 * `tokens.css`, the `--sm-*` custom-property sheet every web app imports
 * first. Dark is the default mode and sits on `:root`; light rides
 * `@media (prefers-color-scheme: light)`. The platform follows the device:
 * there is no in-app theme toggle and nothing is persisted.
 *
 * There is deliberately no `.dark` class, no `.light` class, and no
 * `[data-theme]` hook. A class-based mode switch implies a manual toggle this
 * platform does not have, and it could not reach a Tailwind utility in any
 * case: `tailwind-preset.css` declares the `--color-*` aliases once at
 * `:root`, so overriding `--sm-color-*` further down the tree changes nothing
 * a utility paints. Force a mode the way every review path here already does,
 * through `prefers-color-scheme`: Playwright's `emulateMedia`, or the browser
 * devtools rendering panel. ISSUE-463 tried the classes and removed them.
 */
export const renderTokensCss = (): string =>
  [
    BANNER,
    "",
    ":root {",
    renderModeBlock("dark", "  "),
    "",
    renderStaticBlock("  "),
    "}",
    "",
    "@media (prefers-color-scheme: light) {",
    "  :root {",
    renderModeBlock("light", "    "),
    "  }",
    "}",
    "",
    "/*",
    " * The shared page column. Full width on a phone; stops growing past the cap",
    " * on a desktop so content is never stranded in a sea of empty space.",
    " */",
    ".sm-content-width {",
    "  width: 100%;",
    "  max-width: var(--sm-size-content-width);",
    "  margin-inline: auto;",
    "}",
    "",
  ].join("\n");

/**
 * Seven color roles get shorter Tailwind names than their custom properties.
 *
 * The custom property is `--sm-color-text` because that is what the color IS.
 * But Tailwind's color namespace is prefixed by the property being set, so the
 * literal mapping produces `text-text`, `text-text-secondary`, and
 * `border-border-hairline` — which read as typos in a component file and get
 * "fixed" by the next person who sees one.
 *
 * `ink` is the standard design-system word for the type color family and
 * composes in every direction: `text-ink`, `text-ink-secondary`, `bg-ink` (an
 * inverted tooltip surface). `line` does the same for boundaries:
 * `border-line`, `border-line-strong`, `border-hairline`, `divide-hairline`.
 *
 * These are the ONLY places the two vocabularies differ, and each is a rename,
 * not a second set of values — both names resolve to the same `--sm-color-*`.
 */
const TAILWIND_COLOR_NAME: Readonly<Record<string, string>> = {
  text: "ink",
  textSecondary: "ink-secondary",
  textTertiary: "ink-tertiary",
  textInverse: "ink-inverse",
  border: "line",
  borderStrong: "line-strong",
  borderHairline: "hairline",
};

/**
 * The subset of `tokens.size` that is a length a utility should exist for.
 * Emitted into Tailwind's `--spacing-*` namespace, which feeds `h-*`, `w-*`,
 * `min-h-*`, `p-*`, and `gap-*` alike — so `min-h-touch-target` is the 44px
 * floor and `h-control-height` is the 48px default, both straight from a token.
 *
 * `contentWidth` and `mobileBreakpoint` are deliberately absent: one is a
 * container cap (`--container-*`) and the other is a breakpoint
 * (`--breakpoint-*`), and neither is a spacing value.
 */
const SPACING_SIZE_KEYS = [
  "controlHeight",
  "controlHeightSm",
  "touchTarget",
  "iconSm",
  "iconMd",
  "iconLg",
  "hairline",
  "scoreStatusWidth",
  "scoreStatusCompactWidth",
  "scoreStatusPadding",
  "scoreLiveDot",
  "scoreLiveGap",
  "scoreRowMinHeight",
  "scoreMarkSize",
  "scoreMarkLogoInset",
  "scoreRowInset",
  "scoreRowGap",
  "scoreNameWidth",
  "chipHeight",
  "dayChipWidth",
  "dayChipHeight",
  "dayStripHeight",
  "weekChipMinWidth",
  "gameHeaderMarkSize",
  "screenBottomReserve",
  "pageGutter",
  "scoresCalendarTrack",
] as const satisfies readonly (keyof typeof tokens.size)[];

/**
 * Overlay motion, as named animations.
 *
 * Nothing a person triggered may pop in or out. Radix drives
 * `data-state="open|closed"` on every overlay surface, so the kit only needs a
 * pair of animations per surface shape and a `data-[state=…]:animate-…`
 * variant on the component.
 *
 * These live in the theme rather than in `@sportsmet/ui` for the same reason
 * the colors do: an entrance curve is a platform decision, and a second copy in
 * a component file is a second copy that drifts. Every duration and easing here
 * is a `var()` back to a motion token, never a literal.
 *
 * Written out rather than generated from a table: a keyframe's geometry is not
 * derivable from a token, and a loop that emitted these would be longer and
 * less readable than the CSS it produced.
 */
const OVERLAY_MOTION_LINES: readonly string[] = [
  "  /* Overlay motion. Exits are shorter than entrances — accelerate out, never linger. */",
  "  --animate-fade-in: sm-fade-in var(--sm-motion-mid) var(--sm-ease-enter);",
  "  --animate-fade-out: sm-fade-out var(--sm-motion-mid) var(--sm-ease-exit);",
  "  --animate-sheet-in: sm-sheet-in var(--sm-motion-slow) var(--sm-ease-enter);",
  "  --animate-sheet-out: sm-sheet-out var(--sm-motion-mid) var(--sm-ease-exit);",
  "  --animate-dialog-in: sm-dialog-in var(--sm-motion-slow) var(--sm-ease-enter);",
  "  --animate-dialog-out: sm-dialog-out var(--sm-motion-mid) var(--sm-ease-exit);",
  "  --animate-drawer-left-in: sm-drawer-left-in var(--sm-motion-slow) var(--sm-ease-enter);",
  "  --animate-drawer-left-out: sm-drawer-left-out var(--sm-motion-mid) var(--sm-ease-exit);",
  "  --animate-drawer-right-in: sm-drawer-right-in var(--sm-motion-slow) var(--sm-ease-enter);",
  "  --animate-drawer-right-out: sm-drawer-right-out var(--sm-motion-mid) var(--sm-ease-exit);",
  "  --animate-popover-in: sm-popover-in var(--sm-motion-mid) var(--sm-ease-enter);",
  "  --animate-popover-out: sm-popover-out var(--sm-motion-fast) var(--sm-ease-exit);",
  "  --animate-toast-in: sm-toast-in var(--sm-motion-mid) var(--sm-ease-enter);",
  "  --animate-toast-out: sm-toast-out var(--sm-motion-fast) var(--sm-ease-exit);",
  "  --animate-skeleton: sm-skeleton 1.4s var(--sm-ease-standard) infinite;",
  "",
  "  @keyframes sm-fade-in { from { opacity: 0; } to { opacity: 1; } }",
  "  @keyframes sm-fade-out { from { opacity: 1; } to { opacity: 0; } }",
  "",
  "  /* Bottom sheet: rises from the bottom edge, under 768px. */",
  "  @keyframes sm-sheet-in {",
  "    from { opacity: 0; transform: translateY(100%); }",
  "    to { opacity: 1; transform: translateY(0); }",
  "  }",
  "  @keyframes sm-sheet-out {",
  "    from { opacity: 1; transform: translateY(0); }",
  "    to { opacity: 0; transform: translateY(100%); }",
  "  }",
  "",
  "  /* Centered dialog, 768px and up.",
  "",
  "     THE CENTERING IS CARRIED BY THE `translate` PROPERTY, NOT BY `transform`,",
  "     and these keyframes must never repeat it. Dialog.tsx pairs them with",
  "     `md:-translate-x-1/2 md:-translate-y-1/2`, which Tailwind v4 compiles to",
  "     `translate: var(--tw-translate-x) var(--tw-translate-y)`, the independent",
  "     property rather than a `transform` function. CSS composes `translate` first",
  "     and `transform` after it, so a `translate(-50%, -50%)` in here would STACK on",
  "     the utility's and shove the dialog a second half-width to the left for the",
  "     whole entrance. That was ISSUE-879: the offset applied twice while the",
  "     animation ran and once after it stopped, and the difference snapped back in",
  "     one frame on finish.",
  "",
  "     So `transform` here animates ONLY what this animation owns: a 2% rise and a",
  "     0.97-to-1 scale. Both end states are the identity, which is exactly what",
  "     `transform` reverts to when a non-filling animation stops, so the settled",
  "     geometry and the animation's terminal geometry are the same and nothing",
  "     moves horizontally at either end. Graded by",
  "     packages/ui/src/Dialog/dialogCenteringGeometry.test.tsx. */",
  "  @keyframes sm-dialog-in {",
  "    from { opacity: 0; transform: translateY(2%) scale(0.97); }",
  "    to { opacity: 1; transform: translateY(0) scale(1); }",
  "  }",
  "  @keyframes sm-dialog-out {",
  "    from { opacity: 1; transform: translateY(0) scale(1); }",
  "    to { opacity: 0; transform: translateY(2%) scale(0.97); }",
  "  }",
  "",
  "  @keyframes sm-drawer-left-in {",
  "    from { transform: translateX(-100%); }",
  "    to { transform: translateX(0); }",
  "  }",
  "  @keyframes sm-drawer-left-out {",
  "    from { transform: translateX(0); }",
  "    to { transform: translateX(-100%); }",
  "  }",
  "  @keyframes sm-drawer-right-in {",
  "    from { transform: translateX(100%); }",
  "    to { transform: translateX(0); }",
  "  }",
  "  @keyframes sm-drawer-right-out {",
  "    from { transform: translateX(0); }",
  "    to { transform: translateX(100%); }",
  "  }",
  "",
  "  /* Popovers, dropdowns, tooltips. */",
  "  @keyframes sm-popover-in {",
  "    from { opacity: 0; transform: scale(0.96); }",
  "    to { opacity: 1; transform: scale(1); }",
  "  }",
  "  @keyframes sm-popover-out {",
  "    from { opacity: 1; transform: scale(1); }",
  "    to { opacity: 0; transform: scale(0.96); }",
  "  }",
  "",
  "  @keyframes sm-toast-in {",
  "    from { opacity: 0; transform: translateY(0.5rem); }",
  "    to { opacity: 1; transform: translateY(0); }",
  "  }",
  "  @keyframes sm-toast-out {",
  "    from { opacity: 1; transform: translateY(0); }",
  "    to { opacity: 0; transform: translateY(0.5rem); }",
  "  }",
  "",
  "  /* The skeleton pulse is essential feedback, not decoration, so it keeps",
  "     running under reduced motion — components opt it out individually. */",
  "  @keyframes sm-skeleton {",
  "    0%, 100% { opacity: 1; }",
  "    50% { opacity: 0.55; }",
  "  }",
];

/**
 * `tailwind-preset.css` — the Tailwind v4 `@theme` block. Every value points at
 * a `--sm-*` custom property rather than repeating a literal, so light/dark
 * still resolves at runtime through `tokens.css` and a utility like
 * `bg-surface` is correct in both modes with no `dark:` variant anywhere.
 */
export const renderTailwindPresetCss = (): string => {
  const lines: string[] = [];

  lines.push(`${BANNER}\n`);
  lines.push('@import "tailwindcss";');
  lines.push("");
  lines.push("@theme {");
  lines.push(
    "  /* Colors. `bg-surface`, `text-ink-secondary`, `border-hairline`, … */",
  );
  for (const key of Object.keys(tokens.color.light)) {
    lines.push(
      decl(
        `--color-${TAILWIND_COLOR_NAME[key] ?? kebab(key)}`,
        `var(--sm-color-${kebab(key)})`,
      ),
    );
  }

  lines.push("");
  lines.push(
    "  /* Palette escape hatch — for the gallery and for charts, not for screens. */",
  );
  for (const [family, scale] of Object.entries(palette)) {
    for (const step of Object.keys(scale)) {
      lines.push(
        decl(
          `--color-${kebab(family)}-${step}`,
          `var(--sm-palette-${kebab(family)}-${step})`,
        ),
      );
    }
  }

  lines.push("");
  lines.push(
    "  /* Spacing. Tailwind's numeric ladder lands on 4/8 from this base:",
  );
  lines.push(
    "     p-1=4 p-2=8 p-3=12 p-4=16 p-6=24 p-8=32 p-10=40. Kept in rem so a",
  );
  lines.push(
    "     reader who raises their browser's base font size scales with it. */",
  );
  lines.push(decl("--spacing", "0.25rem"));

  lines.push("");
  lines.push("  /* Radius */");
  for (const key of Object.keys(tokens.radius)) {
    if (key === "none") continue;
    lines.push(
      decl(`--radius-${kebab(key)}`, `var(--sm-radius-${kebab(key)})`),
    );
  }

  lines.push("");
  lines.push(
    "  /* Type. Tailwind v4 pairs a size with its leading and weight. */",
  );
  lines.push(decl("--font-sans", "var(--sm-font-sans)"));
  for (const key of Object.keys(tokens.text)) {
    const name = kebab(key);
    lines.push(decl(`--text-${name}`, `var(--sm-text-${name}-size)`));
    lines.push(
      decl(`--text-${name}--line-height`, `var(--sm-text-${name}-leading)`),
    );
    lines.push(
      decl(`--text-${name}--font-weight`, `var(--sm-text-${name}-weight)`),
    );
  }

  lines.push("");
  lines.push("  /* Elevation */");
  for (const key of Object.keys(tokens.elevation)) {
    lines.push(
      decl(`--shadow-${kebab(key)}`, `var(--sm-elevation-${kebab(key)})`),
    );
  }

  lines.push("");
  lines.push(
    "  /* Motion easings. Durations stay on Tailwind's numeric `duration-*`",
  );
  lines.push(
    `     utilities, which already land exactly on the tokens: duration-${String(tokens.motion.duration.fast)} =`,
  );
  lines.push(
    "     motion-fast, duration-200 = motion-mid, duration-300 = motion-slow. */",
  );
  for (const key of Object.keys(tokens.motion.easing)) {
    lines.push(decl(`--ease-${kebab(key)}`, `var(--sm-ease-${kebab(key)})`));
  }

  lines.push("");
  lines.push(...OVERLAY_MOTION_LINES);

  lines.push("");
  lines.push(
    "  /* Control sizes, in the spacing namespace so they reach h-*, min-h-*, w-*. */",
  );
  for (const key of SPACING_SIZE_KEYS) {
    lines.push(decl(`--spacing-${kebab(key)}`, `var(--sm-size-${kebab(key)})`));
  }

  lines.push("");
  lines.push("  /* The page column cap, as `max-w-content`. */");
  lines.push(decl("--container-content", "var(--sm-size-content-width)"));

  lines.push("");
  lines.push(
    "  /* The two window-size cutoffs: compact under md, expanded at lg and up. */",
  );
  // No --breakpoint-sm, on purpose. Tailwind sorts these px breakpoints ahead
  // of its built-in rem ones, so a sm: utility beside md: or lg: on one
  // property wins at desktop widths. infra/ci/checkBreakpointOrder.ts refuses
  // that pairing, and packages/theme/README.md, section Breakpoints, explains
  // why the fix is not to declare sm here.
  lines.push(decl("--breakpoint-md", `${tokens.size.mobileBreakpoint}px`));
  lines.push(decl("--breakpoint-lg", `${tokens.size.expandedBreakpoint}px`));
  lines.push("}");
  lines.push("");
  lines.push(
    "/* The shared page column, as a Tailwind utility as well as a plain class. */",
  );
  lines.push("@utility sm-content-width {");
  lines.push("  width: 100%;");
  lines.push("  max-width: var(--sm-size-content-width);");
  lines.push("  margin-inline: auto;");
  lines.push("}");
  lines.push("");
  return lines.join("\n");
};
